import store from '@/store'

class WebSocketService {
  constructor() {
    this.socket = null
    this.reconnectInterval = null
    this.isConnecting = false
    this.messageHandlers = new Map()
    this.maxReconnectAttempts = 5
    this.reconnectAttempts = 0
    this.heartbeatInterval = null
  }

  connect() {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('WebSocket ya está conectado')
      return Promise.resolve()
    }

    if (this.isConnecting) {
      console.log('WebSocket ya se está conectando')
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      this.isConnecting = true
      
      // Obtener el token de autenticación
      const token = store.getters['auth/token']
      if (!token) {
        console.error('No hay token de autenticación para WebSocket')
        this.isConnecting = false
        reject(new Error('No authentication token'))
        return
      }

      // Determinar la URL del WebSocket basada en el entorno
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = window.location.host
      const wsUrl = `${protocol}//${host}/ws/whatsapp/?token=${token}`

      console.log('🔌 Conectando WebSocket a:', wsUrl)

      this.socket = new WebSocket(wsUrl)

      this.socket.onopen = () => {
        console.log('✅ WebSocket conectado exitosamente')
        this.isConnecting = false
        this.reconnectAttempts = 0
        this.startHeartbeat()
        resolve()
      }

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          console.log('📨 Mensaje WebSocket recibido:', data)
          this.handleMessage(data)
        } catch (error) {
          console.error('Error parseando mensaje WebSocket:', error)
        }
      }

      this.socket.onclose = (event) => {
        console.log('🔌 WebSocket desconectado:', event.code, event.reason)
        this.isConnecting = false
        this.stopHeartbeat()
        
        if (event.code !== 1000) { // 1000 = cierre normal
          this.scheduleReconnect()
        }
      }

      this.socket.onerror = (error) => {
        console.error('❌ Error en WebSocket:', error)
        this.isConnecting = false
        reject(error)
      }
    })
  }

  disconnect() {
    console.log('🔌 Desconectando WebSocket...')
    this.stopHeartbeat()
    
    if (this.reconnectInterval) {
      clearTimeout(this.reconnectInterval)
      this.reconnectInterval = null
    }

    if (this.socket) {
      this.socket.close(1000, 'Manual disconnect')
      this.socket = null
    }
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Máximo número de intentos de reconexión alcanzado')
      return
    }

    if (this.reconnectInterval) {
      clearTimeout(this.reconnectInterval)
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000) // Exponential backoff max 30s
    this.reconnectAttempts++

    console.log(`🔄 Reintentando conexión WebSocket en ${delay}ms (intento ${this.reconnectAttempts}/${this.maxReconnectAttempts})`)

    this.reconnectInterval = setTimeout(() => {
      this.connect().catch(error => {
        console.error('Error en reconexión:', error)
      })
    }, delay)
  }

  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({
          type: 'ping',
          timestamp: Date.now()
        }))
      }
    }, 30000) // Ping cada 30 segundos
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  handleMessage(data) {
    const { type } = data

    // Manejar pong
    if (type === 'pong') {
      console.log('🏓 Pong recibido del servidor')
      return
    }

    // Ejecutar handlers registrados
    const handlers = this.messageHandlers.get(type)
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data)
        } catch (error) {
          console.error(`Error ejecutando handler para tipo ${type}:`, error)
        }
      })
    } else {
      console.warn(`Sin handler para mensaje de tipo: ${type}`)
    }

    // Eventos globales para Vuex store
    switch (type) {
      case 'new_message':
        store.dispatch('whatsapp/handleNewMessage', data)
        break
      case 'conversation_updated':
        store.dispatch('whatsapp/handleConversationUpdate', data)
        break
      case 'agent_takeover':
        store.dispatch('whatsapp/handleAgentTakeover', data)
        break
      case 'bot_reset':
        store.dispatch('whatsapp/handleBotReset', data)
        break
      default:
        console.log('Tipo de mensaje no manejado globalmente:', type)
    }
  }

  // Registrar handler para un tipo de mensaje específico
  on(messageType, handler) {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, [])
    }
    this.messageHandlers.get(messageType).push(handler)
  }

  // Desregistrar handler
  off(messageType, handler) {
    const handlers = this.messageHandlers.get(messageType)
    if (handlers) {
      const index = handlers.indexOf(handler)
      if (index > -1) {
        handlers.splice(index, 1)
      }
    }
  }

  // Suscribirse a una conversación específica
  subscribeToConversation(conversationId) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({
        type: 'subscribe_to_conversation',
        conversation_id: conversationId
      }))
      console.log(`🔔 Suscrito a conversación ${conversationId}`)
    }
  }

  // Verificar estado de conexión
  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN
  }
}

// Instancia singleton
const websocketService = new WebSocketService()

export default websocketService
